import { COUNTRIES, getCountryName, type CountryOption } from '../constants/countries';
import {
  MANAGED_PAYMENTS_RESTRICTED_COUNTRIES,
  type ManagedPaymentsRestrictedCountry,
} from '../constants/managedPayments';
import {
  getMarketConfigForCountryCode,
  type MarketFeatureFlagState,
} from '../constants/markets';
import type { Currency, Region } from '../types/region';
import { DEFAULT_REGION } from '../types/region';
import { getRegionFromCountryCode } from './regionDetection';

const RESTRICTED_SET = new Set<string>(MANAGED_PAYMENTS_RESTRICTED_COUNTRIES);

/** Countries available for CourtSync sign-up via Stripe Managed Payments. */
export const MANAGED_PAYMENTS_ELIGIBLE_COUNTRIES: CountryOption[] = COUNTRIES.filter((c) =>
  isCountryEligibleForCourtSync(c.code),
);

/**
 * Returns true when the country may sign up for CourtSync via Managed Payments.
 * Empty/null country is treated as eligible (optional at registration).
 */
export function isCountryEligibleForCourtSync(
  code: string | null | undefined,
): boolean {
  if (!code || !code.trim()) {
    return true;
  }
  return !RESTRICTED_SET.has(code.trim().toUpperCase());
}

/**
 * User-facing message when a country is not eligible, or null when eligible.
 */
export function getCourtSyncEligibilityMessage(code: string): string | null {
  const normalized = code.trim().toUpperCase();
  if (!normalized || isCountryEligibleForCourtSync(normalized)) {
    return null;
  }
  const name = getCountryName(normalized);
  const label = name ?? normalized;
  return `CourtSync is not available in ${label} at this time. Please choose a different country or contact support@courtsync.io.`;
}

export function isManagedPaymentsRestrictedCountry(
  code: string,
): code is ManagedPaymentsRestrictedCountry {
  return RESTRICTED_SET.has(code.trim().toUpperCase());
}

/**
 * True when the academy country is outside active MARKET_CONFIG regions (US/AU/GB/EU).
 * Used to show Adaptive Pricing copy (USD list price, local currency at checkout).
 */
export function isAdaptivePricingCountry(
  countryCode: string | null | undefined,
  featureFlags: MarketFeatureFlagState = {},
): boolean {
  if (!countryCode?.trim()) {
    return false;
  }
  return getMarketConfigForCountryCode(countryCode, featureFlags) === null;
}

export interface AcademyPricingLocation {
  country: string | undefined;
  /** Pricing display region (USD list price when outside active markets). */
  region: Region;
  currency: Currency;
}

/**
 * Resolve academy country + pricing region/currency for signup.
 * `country` is authoritative for buyer eligibility; region/currency are for list pricing only.
 */
export function resolveAcademyPricingFromCountry(
  countryCode: string | null | undefined,
  featureFlags: MarketFeatureFlagState = {},
): AcademyPricingLocation {
  if (!countryCode?.trim()) {
    return { country: undefined, region: DEFAULT_REGION, currency: 'USD' };
  }
  const normalized = countryCode.trim().toUpperCase();
  const market = getMarketConfigForCountryCode(normalized, featureFlags);
  if (market) {
    return {
      country: normalized,
      region: market.region,
      currency: market.currency,
    };
  }
  return {
    country: normalized,
    region: getRegionFromCountryCode(normalized, featureFlags),
    currency: 'USD',
  };
}

/** Throws when the country is restricted for Managed Payments sign-up. */
export function assertCountryEligibleForCourtSync(
  code: string | null | undefined,
): void {
  if (code?.trim() && !isCountryEligibleForCourtSync(code)) {
    const message = getCourtSyncEligibilityMessage(code);
    throw new Error(message ?? 'This country is not supported for CourtSync subscriptions.');
  }
}
